import { useMemo } from 'react';

import Gallery from '@website/components/directoryTheme/Gallery';
import CardAccordion from '@website/components/utilities/CardAccordion';
import { TripsPageQuery } from '@website/types/graphqlOperations';

export type ItineraryItemType =
  TripsPageQuery['tripByURL']['itinerary']['items'][0];

export function descriptionTextToHtml(description: string) {
  return description
    .split('\n')
    .filter((i) => i)
    .map((desc) => <p key={desc}>{desc}</p>);
}

type ItineraryItemProps = {
  itineraryItem: ItineraryItemType;
  allItineraryItemImages: ItineraryItemType['media']['items'];
  isOpen: boolean;
  disableCollapse?: boolean;
};

export function ItineraryItem({
  allItineraryItemImages,
  itineraryItem,
  isOpen,
  disableCollapse = false,
}: ItineraryItemProps) {
  const { id, title, description, day, media } = itineraryItem;

  const htmlDescription = useMemo(
    () => descriptionTextToHtml(description),
    [description],
  );

  return (
    <CardAccordion
      key={id}
      title={`Day ${day}: ${title}`}
      className="border-0 px-0"
      headerClassName="border-0 px-0 h5 bg-white"
      bodyClassName="px-0 pt-0 pb-3"
      text="dark"
      open={isOpen}
      disableCollapse={disableCollapse}
    >
      <div data-cy="itinerary-items">{htmlDescription}</div>

      {media?.items && (
        <Gallery
          images={media.items}
          lg={4}
          xs={12}
          allImages={allItineraryItemImages}
        />
      )}
    </CardAccordion>
  );
}

type Props = {
  itineraryItems: ItineraryItemType[];
};

/**
 *
 * ListItineraryItems
 *
 * Show a list of itinerary items
 */
export default function ListItineraryItems({ itineraryItems }: Props) {
  const showAll = itineraryItems.length <= 5;

  // take all media from all itinerary items and flatten it
  const allItineraryImages = useMemo(() => {
    const images = itineraryItems.reduce((acc, item) => {
      if (!item.media?.items) {
        return acc;
      }
      return [...acc, ...item.media.items];
    }, [] as any);

    return images;
  }, [itineraryItems]);

  return (
    <>
      {itineraryItems &&
        itineraryItems.map((item, index) => (
          <ItineraryItem
            disableCollapse={!!showAll}
            key={item.id}
            itineraryItem={item}
            isOpen={index === 0 || showAll}
            allItineraryItemImages={allItineraryImages}
          />
        ))}
    </>
  );
}
